import React from 'react'
import { Box, Tooltip } from '@mui/material'
import Txt from './Txt'
import { Restaurant } from '../utils/restaurant'

type KioskHeartbeat = NonNullable<Restaurant['kioskHeartbeats']>[number]

const DAY_COUNT = 7
const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const AXIS_HOURS = new Set([0, 6, 12, 18])

// Der Kiosk fragt jede Minute: bis zu 60 Heartbeats pro Stunde. Grau = keiner, darüber eine
// blaue Ordinal-Rampe (geprüft mit dataviz validate_palette --ordinal).
const LEVELS = [
	{ min: 50, color: '#1c5cab', label: '50–60 Min.' },
	{ min: 30, color: '#3987e5', label: '30–49 Min.' },
	{ min: 1, color: '#86b6ef', label: '1–29 Min.' },
	{ min: 0, color: '#e4e2df', label: 'kein Heartbeat' },
] as const

// "2026-10-29 18:15:00" — dieselbe Ortszeit, in der die API die Stunden zählt.
const toBerlinString = (date: Date): string =>
	date.toLocaleString('sv-SE', { timeZone: 'Europe/Berlin' })

const sxDevice = { display: 'flex', flexDirection: 'column', gap: '0.5rem' } as const
const sxGrid = {
	display: 'grid',
	gridTemplateColumns: '5.5rem repeat(24, minmax(0, 1fr))',
	gap: '2px',
	alignItems: 'center',
} as const
const sxAxisLabel = { color: 'text.secondary', lineHeight: 1 } as const
const sxLegend = { display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' } as const
const sxLegendItem = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem' } as const

const DeviceHeatmap = React.memo(
	({ heartbeat }: { heartbeat: KioskHeartbeat }): JSX.Element => {
		const rows = React.useMemo(() => {
			const countByHour = new Map(heartbeat.hours.map(({ hour, count }) => [hour, count]))
			const now = toBerlinString(new Date())
			const nowHour = `${now.slice(0, 10)}T${now.slice(11, 13)}`
			const [year, month, day] = now.slice(0, 10).split('-').map(Number)
			// Mittags verankert, damit eine Zeitumstellung keinen Tag doppelt oder gar nicht liefert.
			return Array.from({ length: DAY_COUNT }, (_, index) => {
				const date = new Date(Date.UTC(year, month - 1, day - (DAY_COUNT - 1 - index), 12))
				const dayKey = toBerlinString(date).slice(0, 10)
				const dayLabel = date.toLocaleDateString('de-DE', {
					timeZone: 'Europe/Berlin',
					weekday: 'short',
					day: '2-digit',
					month: '2-digit',
				})
				return {
					dayKey,
					dayLabel,
					cells: HOURS.map((hour) => {
						const hourKey = `${dayKey}T${String(hour).padStart(2, '0')}`
						const count = Math.min(countByHour.get(hourKey) ?? 0, 60)
						const isFuture = hourKey > nowHour
						return {
							hourKey,
							title: isFuture
								? `${dayLabel}, ${hour}–${hour + 1} Uhr: noch nicht erreicht`
								: `${dayLabel}, ${hour}–${hour + 1} Uhr: ${count} von 60 Minuten`,
							sx: {
								height: '1.1rem',
								borderRadius: '2px',
								bgcolor: isFuture
									? 'transparent'
									: LEVELS.find((level) => count >= level.min)?.color,
							},
						}
					}),
				}
			})
		}, [heartbeat.hours])

		return (
			<Box sx={sxDevice}>
				<Txt variant="body2" fontWeight="bold">
					{`Gerät ${heartbeat.deviceId} · Kiosk ${heartbeat.kioskVersion ?? '–'}`}
				</Txt>
				<Box sx={sxGrid} role="img" aria-label={`Heartbeats der letzten ${DAY_COUNT} Tage`}>
					{rows.map((row) => (
						<React.Fragment key={row.dayKey}>
							<Txt variant="caption">{row.dayLabel}</Txt>
							{row.cells.map((cell) => (
								<Tooltip key={cell.hourKey} title={cell.title} placement="top" arrow>
									<Box sx={cell.sx} />
								</Tooltip>
							))}
						</React.Fragment>
					))}
					<Box />
					{HOURS.map((hour) => (
						<Txt key={hour} variant="caption" sx={sxAxisLabel}>
							{AXIS_HOURS.has(hour) ? `${hour}` : ''}
						</Txt>
					))}
				</Box>
			</Box>
		)
	}
)
DeviceHeatmap.displayName = 'DeviceHeatmap'

const LEGEND_SWATCH_SX = LEVELS.map((level) => ({
	label: level.label,
	sx: { width: '0.9rem', height: '0.9rem', borderRadius: '2px', bgcolor: level.color },
})).reverse()

const KioskHeartbeatChart = ({
	heartbeats,
}: {
	heartbeats: KioskHeartbeat[]
}): JSX.Element => (
	<Box display="flex" flexDirection="column" gap="1.5rem">
		<Txt variant="h6" fontWeight="bold">
			{`Kiosk-Heartbeat der letzten ${DAY_COUNT} Tage`}
		</Txt>
		{heartbeats.length ? (
			heartbeats.map((heartbeat) => <DeviceHeatmap key={heartbeat.deviceId} heartbeat={heartbeat} />)
		) : (
			<Txt variant="body2" color="text.secondary">
				{`Kein Kiosk-Gerät in den letzten ${DAY_COUNT} Tagen`}
			</Txt>
		)}
		{heartbeats.length ? (
			<Box sx={sxLegend}>
				<Txt variant="caption" color="text.secondary">
					Minuten mit Heartbeat pro Stunde:
				</Txt>
				{LEGEND_SWATCH_SX.map((item) => (
					<Box key={item.label} sx={sxLegendItem}>
						<Box sx={item.sx} />
						<Txt variant="caption">{item.label}</Txt>
					</Box>
				))}
			</Box>
		) : null}
	</Box>
)

export default React.memo(KioskHeartbeatChart)
